import { computed, observable, action as mobxAction } from 'mobx';
import { Action, ActionStatus } from './action';
import type { BaseStore } from '~/stores/base.store';

const MX_STACK_SIZE = 50;

export class ActionsStack {
    rootStore: BaseStore;
    @observable _stack: Array<Action>;
    @observable _currentItemIndex: number;

    constructor(rootStore: BaseStore) {
        this.rootStore = rootStore;

        this.clear();
    }

    get stack() {
        return this._stack;
    }

    @computed
    get isUndoAvailable() {
        return this._currentItemIndex !== -1;
    }

    @computed
    get isRedoAvailable() {
        const nextAction = this.getNextAction();
        if (!nextAction) {
            return false;
        }
        return (
            !nextAction.isFailedStatus &&
            this._stack.length - 1 > this._currentItemIndex
        );
    }

    @computed
    get isStackEmpty() {
        return !this._stack.length;
    }

    @computed
    get currentAction() {
        if (!this._stack.length) {
            return null;
        }

        return this._stack[this._currentItemIndex];
    }

    get undoActionName() {
        return 'Undo';
        // currently disabled, awaiting future feature FLOW-2987
        // return this.getCurrentAction()?.name;
    }

    get redoActionName() {
        return 'Redo';
        // currently disabled, awaiting future feature FLOW-2987
        // return this.getNextAction()?.name;
    }

    @mobxAction
    push(action: Action) {
        this.setFailedActionToOppositeResolvedStatus();
        this.overrideUndoActions();

        this._stack.push(action);

        if (this._stack.length > MX_STACK_SIZE) {
            this._stack.shift();
        }

        this._currentItemIndex = this._stack.length - 1;
    }

    @mobxAction
    clear() {
        this._stack = [];
        this._currentItemIndex = -1;
    }

    getNextAction() {
        if (this._currentItemIndex === this._stack.length - 1) {
            return null;
        }

        const nextAction = this._stack[this._currentItemIndex + 1];
        if (
            this._currentItemIndex >= this._stack.length - 1 ||
            nextAction.status === ActionStatus.redoResolved ||
            nextAction.status === ActionStatus.redoLocally ||
            nextAction.status === ActionStatus.applyFailed ||
            nextAction.status === ActionStatus.revertFailed ||
            nextAction.status === ActionStatus.redoFailed
        ) {
            return null;
        }

        return nextAction;
    }

    returnToLastSucceedAction() {
        if (!this._stack.length) {
            return null;
        }

        this.setFailedActionToOppositeResolvedStatus();

        while (
            this._currentItemIndex >= 0 &&
            (this._stack[this._currentItemIndex].status ===
                ActionStatus.applyFailed ||
                this._stack[this._currentItemIndex].status ===
                    ActionStatus.revertFailed ||
                this._stack[this._currentItemIndex].status ===
                    ActionStatus.redoFailed)
        ) {
            this._currentItemIndex--;
        }

        const currentAction = this._stack[this._currentItemIndex];
        if (
            !this._stack.length ||
            this._currentItemIndex < 0 ||
            currentAction.status === ActionStatus.revertedResolved ||
            currentAction.status === ActionStatus.revertedLocally
        ) {
            return null;
        }

        return currentAction;
    }

    removeAction(action: Action) {
        this._stack = this._stack.filter(x => x !== action);
    }

    removeActionFromNumber(actionNumber: number) {
        this._stack = this._stack.filter(x => x.action_number < actionNumber);
    }

    findActionByNumber(actionNumber: number): Action {
        return this._stack.find(x => x.actionNumbersMap[actionNumber]);
    }

    getWaitingToSendActions() {
        return this.stack.filter(x => x.isLocalStatus && !x.isSentToServer);
    }

    getUnresolvedSentActions() {
        return this.stack.filter(x => x.isSentToServer && !x.isResolvedStatus);
    }

    getWaitingActions() {
        return [
            ...this.getUnresolvedSentActions(),
            ...this.getWaitingToSendActions()
        ];
    }

    @mobxAction
    onAfterRedo() {
        this.setFailedActionToOppositeResolvedStatus();
        this._currentItemIndex++;
    }

    @mobxAction
    onAfterUndo() {
        this._currentItemIndex--;
    }

    @mobxAction
    private overrideUndoActions() {
        if (this._currentItemIndex === this._stack.length - 1) {
            return;
        }

        this._stack.splice(this._currentItemIndex + 1);
    }

    @mobxAction
    moveToAction(lastActionNumber: number) {
        this._currentItemIndex = this._stack.findIndex(
            x => x.actionNumbersMap[lastActionNumber]
        );
    }

    @mobxAction
    private setFailedActionToOppositeResolvedStatus() {
        // We are optimistic that we managed to rollback the reverted action so now its in status redo.
        if (this.currentAction?.status !== ActionStatus.revertFailed) {
            return;
        }

        this.currentAction.setStatus(ActionStatus.redoResolved);
    }
}
